import React, { useState } from 'react';
import {
    Container,
    Grid,
    Card,
    CardContent,
    Typography,
    Button,
    Box,
} from '@mui/material';
import {
    Storefront,
    Inventory,
    AccountBalance,
    LocalShipping,
    People,
    Dashboard,
} from '@mui/icons-material';
import CreateInstitution from '../institutions/CreateInstitution';
import PricingSection from './PricingSection';
import LoginPopover from '../auth/LoginPopover';
import './LandingPage.css';

const features = [
    {
        icon: <Storefront sx={{ fontSize: 48, color: '#007bff' }} />,
        title: 'Multi-Store Retail',
        description: 'Sales invoices, quotations and receipts across every store you run.',
    },
    {
        icon: <Inventory sx={{ fontSize: 48, color: '#007bff' }} />,
        title: 'Stock & Stocktake',
        description: 'Items, stock levels, stocktakes and stock transfers between stores.',
    },
    {
        icon: <LocalShipping sx={{ fontSize: 48, color: '#007bff' }} />,
        title: 'Procurement',
        description: 'Suppliers, purchase orders, goods receipts, bills and payments.',
    },
    {
        icon: <AccountBalance sx={{ fontSize: 48, color: '#007bff' }} />,
        title: 'Accounting',
        description: 'Chart of accounts, general ledger, bank and financial reports.',
    },
    {
        icon: <People sx={{ fontSize: 48, color: '#007bff' }} />,
        title: 'Customers',
        description: 'Customer records tied to every sale, quotation and receipt.',
    },
    {
        icon: <Dashboard sx={{ fontSize: 48, color: '#007bff' }} />,
        title: 'Dashboards & Reports',
        description: 'Role-based analytics across sales, stock and accounts.',
    },
];

const LandingPage: React.FC = () => {
    const [showCreateInstitutionModal, setShowCreateInstitutionModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [loginAnchorEl, setLoginAnchorEl] = useState<HTMLElement | null>(null);
    const [selectedPlan, setSelectedPlan] = useState<string | undefined>(undefined);

    const openCreateModal = (plan?: string) => {
        setSelectedPlan(plan);
        setShowCreateInstitutionModal(true);
    };

    const handleCreateInstitutionSuccess = (message?: string, email?: string) => {
        setShowCreateInstitutionModal(false);
        let successMsg = message || 'Institution created successfully!';
        if (email) {
            successMsg += ` Login credentials will be sent to: ${email}`;
        }
        setSuccessMessage(successMsg);
    };


    return (
        <div className="landing-page">
            {/* Hero Section */}
            <section className="landing-hero">
                <Container maxWidth="md">
                    <Box textAlign="center" py={10}>
                        <h1 className="landing-hero-title">
                            Run Your Retail Business on Kervolt POS
                        </h1>
                        <Typography
                            variant="h6"
                            sx={{ color: 'rgba(255,255,255,0.9)', mb: 4, fontWeight: 300 }}
                        >
                            A multi-store point-of-sale for Bangladeshi retail — sales, stock,
                            procurement, customers and accounting in one place.
                        </Typography>
                        {successMessage && (
                            <Box sx={{
                                backgroundColor: 'rgba(40, 167, 69, 0.9)',
                                color: 'white',
                                p: 2,
                                borderRadius: 1,
                                mb: 3,
                                maxWidth: 500,
                                mx: 'auto',
                            }}>
                                {successMessage}
                            </Box>
                        )}
                        <Box display="flex" gap={2} justifyContent="center">
                            <Button
                                variant="contained"
                                size="large"
                                onClick={() => openCreateModal()}
                                sx={{
                                    backgroundColor: '#28a745',
                                    '&:hover': { backgroundColor: '#218838' },
                                    textTransform: 'none',
                                    px: 4,
                                    py: 1.5,
                                    fontSize: '1rem',
                                }}
                            >
                                Get Started
                            </Button>
                            <Button
                                variant="outlined"
                                size="large"
                                onClick={(e) => setLoginAnchorEl(e.currentTarget)}
                                sx={{
                                    borderColor: 'white',
                                    color: 'white',
                                    '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.1)' },
                                    textTransform: 'none',
                                    px: 4,
                                    py: 1.5,
                                    fontSize: '1rem',
                                }}
                            >
                                Login
                            </Button>
                        </Box>
                    </Box>
                </Container>
            </section>

            {/* Features Section */}
            <section className="landing-features">
                <Container maxWidth="lg">
                    <Box textAlign="center" mb={6}>
                        <h2 className="landing-section-title">
                            Everything You Need to Run the Back Office
                        </h2>
                        <Typography variant="body1" color="text.secondary" maxWidth={600} mx="auto">
                            Built for retail, from a single shop to a multi-store chain —
                            invoice, stock, procure and account for every taka.
                        </Typography>
                    </Box>
                    <Grid container spacing={3}>
                        {features.map((feature) => (
                            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={feature.title}>
                                <Card
                                    sx={{
                                        height: '100%',
                                        textAlign: 'center',
                                        transition: 'transform 0.2s, box-shadow 0.2s',
                                        '&:hover': {
                                            transform: 'translateY(-4px)',
                                            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                                        },
                                    }}
                                    elevation={1}
                                >
                                    <CardContent sx={{ p: 3 }}>
                                        <Box mb={2}>{feature.icon}</Box>
                                        <Typography variant="h6" fontWeight={600} gutterBottom>
                                            {feature.title}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {feature.description}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </section>

            {/* Pricing Section */}
            <PricingSection onGetStarted={openCreateModal} />

            {/* CTA Section */}
            <section className="landing-cta">
                <Container maxWidth="sm">
                    <Box textAlign="center" py={8}>
                        <h2 className="landing-cta-title">
                            Ready to Get Started?
                        </h2>
                        <Typography variant="body1" color="text.secondary" mb={3}>
                            Set up your store in minutes and start recording sales today.
                        </Typography>
                        <Button
                            variant="contained"
                            size="large"
                            onClick={() => openCreateModal()}
                            sx={{
                                backgroundColor: '#28a745',
                                '&:hover': { backgroundColor: '#218838' },
                                textTransform: 'none',
                                px: 5,
                                py: 1.5,
                                fontSize: '1rem',
                            }}
                        >
                            Create New Institution
                        </Button>
                    </Box>
                </Container>
            </section>

            {/* Footer */}
            <footer className="landing-footer">
                <Container maxWidth="lg">
                    <Typography variant="body2" color="text.secondary" textAlign="center">
                        &copy; {new Date().getFullYear()} Kervolt POS. All rights reserved.
                    </Typography>
                </Container>
            </footer>

            <CreateInstitution
                isModal={true}
                selectedPlan={selectedPlan}
                open={showCreateInstitutionModal}
                onClose={() => setShowCreateInstitutionModal(false)}
                onSuccess={handleCreateInstitutionSuccess}
            />
            <LoginPopover
                anchorEl={loginAnchorEl}
                open={Boolean(loginAnchorEl)}
                onClose={() => setLoginAnchorEl(null)}
            />
        </div>
    );
};

export default LandingPage;
